import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import "./SalaryView.css";

const MONTHS = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const MONTH_ABBR: Record<string,string> = {
  April:"Apr",May:"May",June:"Jun",July:"Jul",August:"Aug",September:"Sep",
  October:"Oct",November:"Nov",December:"Dec",January:"Jan",February:"Feb",March:"Mar",
};
const ALL_MONTH_OPTIONS = MONTHS.map((m, i) => ({ label: MONTH_ABBR[m], value: i + 1 }));

// First editable fiscal month index per forecast dimension (same as BudgetEditor)
const DIMENSION_START_MONTH: Record<string, number> = {
  "July Forecast": 3,
  "October Forecast": 6,
  "January Forecast": 9,
};

interface SalaryConfig {
  raise_before_m3: number;
  raise_after_m3: number;
  bip_raise: number;
}
interface Employee {
  id?: number;
  cost_center_number: string;
  employee_name: string;
  job_title: string;
  annual_salary: number;
  employment_type: "Full-Time" | "Part-Time" | "Temp";
  bip_pct: number;
  hire_month: number;
  status: "active" | "terminated";
  termination_month: number;
  is_new_hire?: boolean;
}

interface SalaryViewProps {
  costCenterNumber: string;
  isAdmin?: boolean;
  onDataSaved?: () => void;
}

// Monthly salary — Temp employees get no raise (flat salary/12)
// termination_month: 0 = N/A, 1-12 = last paid fiscal month
function computeMonthly(
  salary: number,
  raise_before: number,
  raise_after: number,
  hire_month = 0,
  employment_type = "Full-Time",
  termination_month = 0
): Record<string, number> {
  const result: Record<string, number> = {};
  MONTHS.forEach((m, i) => {
    const fiscalMonth = i + 1;
    if (hire_month > 0 && fiscalMonth < hire_month) {
      result[m] = 0; // not yet hired
    } else if (termination_month > 0 && fiscalMonth > termination_month) {
      result[m] = 0; // terminated — past last paid month
    } else {
      const raise = employment_type === "Temp" ? 0 : (i < 3 ? raise_before : raise_after);
      result[m] = Math.round(salary * (1 + raise) / 12);
    }
  });
  return result;
}

function computeBipMonthly(
  salary: number,
  raise_before: number,
  raise_after: number,
  bip_pct: number,
  bip_raise: number,
  hire_month = 0,
  termination_month = 0,
  status = "active"
): Record<string, number> {
  const result: Record<string, number> = {};
  MONTHS.forEach((m, i) => {
    const fiscalMonth = i + 1;
    if (hire_month > 0 && fiscalMonth < hire_month) {
      result[m] = 0;
    } else if (status === "terminated" && termination_month > 0 && fiscalMonth > termination_month) {
      result[m] = 0;
    } else {
      const raise = i < 3 ? raise_before : raise_after;
      result[m] = Math.round(salary * (1 + raise) * bip_pct * (1 + bip_raise) / 12);
    }
  });
  return result;
}

const fmt = (n: number) =>
  n === 0 ? "–" : "$" + Math.round(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const emptyEmployee = (cc: string): Employee => ({
  cost_center_number: cc,
  employee_name: "",
  job_title: "",
  annual_salary: 0,
  employment_type: "Full-Time",
  bip_pct: 0,
  hire_month: 0,
  status: "active",
  termination_month: 0,
  is_new_hire: false,
});

const emptyNewHire = (cc: string): Employee => ({
  ...emptyEmployee(cc),
  hire_month: 1,
  is_new_hire: true,
});

const SalaryView: React.FC<SalaryViewProps> = ({ costCenterNumber, isAdmin, onDataSaved }) => {
  const [config, setConfig] = useState<SalaryConfig>({ raise_before_m3: 0, raise_after_m3: 0.035, bip_raise: 0 });
  const [existing, setExisting] = useState<Employee[]>([]);
  const [newHires, setNewHires] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [forecastDim, setForecastDim] = useState<string | null>(null);
  const [forecastActive, setForecastActive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ section: "existing" | "newHire"; idx: number; name: string } | null>(null);
  const [bipDrafts, setBipDrafts] = useState<Record<string, string>>({});
  const [dragSource, setDragSource] = useState<{ section: "existing" | "newHire"; idx: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<"existing" | "newHire" | null>(null);
  const [computedAccounts, setComputedAccounts] = useState<{ account: string; label: string; months: Record<string, number>; total: number }[]>([]);

  // Snapshot of last-saved state for dirty detection
  const savedSnapshot = useRef<string>("");

  const serializeEmployees = (ex: Employee[], nh: Employee[]) =>
    JSON.stringify([...ex.map(e => ({ ...e, hire_month: e.hire_month || 0, status: e.status || "active", termination_month: e.termination_month || 0 })), ...nh]);

  const isDirty = !loading && serializeEmployees(existing, newHires) !== savedSnapshot.current;

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, empRes] = await Promise.all([
        api.get("/salary/config"),
        api.get(`/salary/employees?cost_center=${costCenterNumber}`),
      ]);
      setConfig(cfgRes.data);
      const all: Employee[] = empRes.data.map((e: any) => ({
        ...e,
        status: e.status || "active",
        termination_month: e.termination_month || 0,
      }));
      const nonTemp = all.filter(e => e.employment_type !== "Temp");
      const ex = nonTemp.filter(e => !e.is_new_hire);
      const nh = nonTemp.filter(e => !!e.is_new_hire);
      setExisting(ex);
      setNewHires(nh);
      savedSnapshot.current = serializeEmployees(ex, nh);
    } catch {
      setFeedback({ ok: false, msg: "Failed to load salary data." });
    } finally {
      setLoading(false);
    }
  }, [costCenterNumber]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        setForecastActive(!!res.data.active);
        if (res.data.dimension) setForecastDim(res.data.dimension);
      })
      .catch(() => {});
  }, []);

  const loadComputed = useCallback(() => {
    if (!costCenterNumber) return;
    api.get(`/salary/computed?cost_center=${costCenterNumber}`)
      .then(res => {
        const data = res.data as { account: string; label: string; months: Record<string, number>; total: number }[];
        setComputedAccounts(data.filter(a => a.account === "60100" || a.account === "60120" || a.account === "60130"));
      })
      .catch(() => {});
  }, [costCenterNumber]);
  useEffect(() => { loadComputed(); }, [loadComputed]);

  // Month options restricted to the current forecast window (forward-looking only)
  const monthOptions = (() => {
    const startIdx = forecastDim ? (DIMENSION_START_MONTH[forecastDim] ?? 0) : 0;
    return ALL_MONTH_OPTIONS.filter(o => o.value > startIdx);
  })();
  const firstAllowedMonth = monthOptions[0]?.value ?? 1;

  const addExisting = () => setExisting(prev => [...prev, emptyEmployee(costCenterNumber)]);
  const addNewHire  = () => setNewHires(prev => [...prev, { ...emptyNewHire(costCenterNumber), hire_month: firstAllowedMonth }]);

  const removeExisting = (idx: number) => setExisting(prev => prev.filter((_, i) => i !== idx));
  const removeNewHire  = (idx: number) => setNewHires(prev => prev.filter((_, i) => i !== idx));

  const requestRemove = (section: "existing" | "newHire", idx: number, name: string) =>
    setDeleteConfirm({ section, idx, name: name || (section === "newHire" ? "this new hire" : "this employee") });

  const confirmRemove = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.section === "existing") removeExisting(deleteConfirm.idx);
    else removeNewHire(deleteConfirm.idx);
    setDeleteConfirm(null);
  };

  const updateExisting = (idx: number, field: keyof Employee, value: string | number) =>
    setExisting(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  const updateNewHire  = (idx: number, field: keyof Employee, value: string | number) =>
    setNewHires(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const handleDrop = (targetSection: "existing" | "newHire") => {
    if (!dragSource || dragSource.section === targetSection) {
      setDragSource(null);
      setDropTarget(null);
      return;
    }
    if (dragSource.section === "existing") {
      const emp = existing[dragSource.idx];
      if (emp) {
        setExisting(prev => prev.filter((_, i) => i !== dragSource.idx));
        setNewHires(prev => [...prev, { ...emp, is_new_hire: true, status: "active", termination_month: 0, hire_month: emp.hire_month || firstAllowedMonth }]);
      }
    } else {
      const emp = newHires[dragSource.idx];
      if (emp) {
        setNewHires(prev => prev.filter((_, i) => i !== dragSource.idx));
        setExisting(prev => [...prev, { ...emp, is_new_hire: false }]);
      }
    }
    setDragSource(null);
    setDropTarget(null);
  };

  const handleSave = async () => {
    const allEmps = [...existing, ...newHires];
    const emptyNames = allEmps.filter(e => !e.employee_name.trim());
    if (emptyNames.length > 0) {
      setFeedback({ ok: false, msg: `${emptyNames.length} employee(s) missing a name.` });
      return;
    }
    const badTerm = allEmps.find(e => e.status === "terminated" && e.termination_month > 0 && e.hire_month > 0 && e.termination_month < e.hire_month);
    if (badTerm) {
      setFeedback({ ok: false, msg: `${badTerm.employee_name || "Employee"}: end month is before start month.` });
      return;
    }
    setSaving(true);
    setFeedback(null);
    const all = [
      ...existing.map(e => ({ ...e, is_new_hire: false })),
      ...newHires.map(e => ({ ...e, is_new_hire: true })),
    ];
    try {
      await api.post("/salary/employees", {
        cost_center: costCenterNumber,
        employees: all.map(e => ({
          employee_name:     e.employee_name,
          job_title:         e.job_title,
          annual_salary:     e.annual_salary,
          employment_type:   e.employment_type,
          bip_pct:           e.bip_pct,
          hire_month:        e.hire_month,
          status:             e.status || "active",
          termination_month:  e.termination_month || 0,
          contract_end_month: (e as any).contract_end_month || 0,
          hours_per_week:     (e as any).hours_per_week || 0,
          is_new_hire:        e.is_new_hire || false,
        })),
      });
      setFeedback({ ok: true, msg: "Saved successfully." });
      load();
      setTimeout(loadComputed, 500);
      onDataSaved?.();
    } catch (err: any) {
      const detail = err?.response?.data?.error || err?.message || "";
      setFeedback({ ok: false, msg: detail ? `Save failed: ${detail}` : "Save failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!savedSnapshot.current) return;
    const all = JSON.parse(savedSnapshot.current) as Employee[];
    setExisting(all.filter(e => !e.is_new_hire));
    setNewHires(all.filter(e => !!e.is_new_hire));
    setFeedback(null);
    setBipDrafts({});
  };

  // ── Compute monthly values + summary rows ──
  const addBip = (emp: Employee) => ({
    ...emp,
    monthly: computeMonthly(emp.annual_salary, config.raise_before_m3, config.raise_after_m3, emp.hire_month, emp.employment_type, emp.termination_month),
    bipMonthly: computeBipMonthly(emp.annual_salary, config.raise_before_m3, config.raise_after_m3, emp.bip_pct, config.bip_raise, emp.hire_month, emp.termination_month, emp.status),
  });
  const existingWithMonthly = existing.map(addBip);
  const newHiresWithMonthly = newHires.map(addBip);

  const allWithMonthly = [...existingWithMonthly, ...newHiresWithMonthly];

  const monthlyTotals: Record<string, number> = {};
  const bipMonthlyTotals: Record<string, number> = {};
  const monthlyHeadcount: Record<string, number> = {};
  MONTHS.forEach(m => { monthlyTotals[m] = 0; bipMonthlyTotals[m] = 0; monthlyHeadcount[m] = 0; });
  allWithMonthly.forEach((emp, i) => {
    const isNewHire = i >= existingWithMonthly.length;
    MONTHS.forEach((m, mIdx) => {
      monthlyTotals[m] += emp.monthly[m];
      bipMonthlyTotals[m] += emp.bipMonthly[m];
      const fiscalMonth = mIdx + 1;
      const hm = isNewHire ? (emp.hire_month || 1) : 0;
      const tm = emp.termination_month || 0;
      if (emp.employment_type === "Full-Time" && (hm === 0 || fiscalMonth >= hm) && (tm === 0 || fiscalMonth <= tm)) {
        monthlyHeadcount[m]++;
      }
    });
  });
  const annualTotal = MONTHS.reduce((s, m) => s + monthlyTotals[m], 0);
  const bipAnnualTotal = MONTHS.reduce((s, m) => s + bipMonthlyTotals[m], 0);

  const editingDisabled = false;

  if (loading) return <div className="salary-loading">Loading salary data…</div>;

  // Column count: name + title + salary + bip% + type + start + status + end + 12 months + total + del = 21
  const colCount = 21;

  const employeeTableBody = (
    rows: typeof existingWithMonthly,
    onUpdate: (idx: number, field: keyof Employee, value: string | number) => void,
    section: "existing" | "newHire",
    isNewHire: boolean
  ) => rows.map((emp, idx) => (
    <tr
      key={idx}
      className={`salary-row${emp.status === "terminated" ? " salary-row--terminated" : ""}${dragSource?.section === section && dragSource?.idx === idx ? " salary-row--dragging" : ""}`}
      draggable={!editingDisabled}
      onDragStart={e => {
        setDragSource({ section, idx });
        e.dataTransfer.effectAllowed = "move";
        if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = "0.4";
      }}
      onDragEnd={e => {
        setDragSource(null);
        setDropTarget(null);
        if (e.currentTarget instanceof HTMLElement) e.currentTarget.style.opacity = "";
      }}
    >
      <td className="col-name">
        <input
          className="salary-input"
          value={emp.employee_name}
          placeholder={isNewHire ? "New hire name" : "Employee name"}
          aria-label="Employee name"
          onChange={e => onUpdate(idx, "employee_name", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-title">
        <input
          className="salary-input"
          value={emp.job_title}
          placeholder="Job title"
          aria-label="Job title"
          onChange={e => onUpdate(idx, "job_title", e.target.value)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-salary">
        <input
          className="salary-input salary-input--num"
          type="number" min={0} step={1000}
          value={emp.annual_salary || ""}
          placeholder="0"
          aria-label="Annual salary"
          onChange={e => onUpdate(idx, "annual_salary", parseFloat(e.target.value) || 0)}
          disabled={editingDisabled}
        />
      </td>
      <td className="col-bip-pct">
        <div className="bip-pct-input-wrap">
          <input
            className="salary-input salary-input--num"
            type="number" min={0} max={100} step={0.5}
            value={`${section}-${idx}` in bipDrafts ? bipDrafts[`${section}-${idx}`] : (emp.bip_pct > 0 ? +(emp.bip_pct * 100).toFixed(1) : "")}
            placeholder="0"
            style={{ width: 60, textAlign: "center" }}
            disabled={editingDisabled}
            onFocus={() => setBipDrafts(prev => ({
              ...prev,
              [`${section}-${idx}`]: emp.bip_pct > 0 ? String(+(emp.bip_pct * 100).toFixed(1)) : "",
            }))}
            onChange={e => setBipDrafts(prev => ({ ...prev, [`${section}-${idx}`]: e.target.value }))}
            onBlur={e => {
              const val = parseFloat(e.target.value) || 0;
              onUpdate(idx, "bip_pct", val / 100);
              setBipDrafts(prev => { const n = { ...prev }; delete n[`${section}-${idx}`]; return n; });
            }}
          />
          <span className="bip-pct-symbol">%</span>
        </div>
      </td>
      <td className="col-type">
        <select
          className={`salary-chip-select salary-chip--type${emp.employment_type === "Temp" ? " salary-chip--warn" : ""}`}
          value={emp.employment_type}
          onChange={e => onUpdate(idx, "employment_type", e.target.value as Employee["employment_type"])}
          disabled={editingDisabled}
        >
          <option value="Full-Time">Full-Time</option>
          <option value="Part-Time">Part-Time</option>
        </select>
      </td>
      <td className="col-hire-month">
        <select
          className="salary-chip-select salary-chip--month"
          value={emp.hire_month}
          onChange={e => onUpdate(idx, "hire_month", parseInt(e.target.value))}
          disabled={editingDisabled}
        >
          {!isNewHire && <option value={0}>Full Year</option>}
          {ALL_MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </td>
      <td className="col-status">
        {isNewHire ? (
          <span className="salary-chip-select salary-chip--planned-label">Planned</span>
        ) : (
          <select
            className={`salary-chip-select salary-chip--status${emp.status === "terminated" ? " salary-chip--danger" : " salary-chip--ok"}`}
            value={emp.status || "active"}
            onChange={e => {
              onUpdate(idx, "status", e.target.value as Employee["status"]);
              if (e.target.value === "active") onUpdate(idx, "termination_month", 0);
            }}
            disabled={editingDisabled}
          >
            <option value="active">Active</option>
            <option value="terminated">Inactive/Moved</option>
          </select>
        )}
      </td>
      <td className="col-end-month">
        {!isNewHire && emp.status === "terminated" ? (
          <select
            className="salary-chip-select salary-chip--month"
            value={emp.termination_month || 0}
            onChange={e => onUpdate(idx, "termination_month", parseInt(e.target.value))}
            disabled={editingDisabled}
          >
            <option value={0}>Select…</option>
            {ALL_MONTH_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <span className="salary-cell-muted">–</span>
        )}
      </td>
      {MONTHS.map(m => (
        <td key={m} className="col-month salary-calc-cell">
          {emp.monthly[m] > 0 ? fmt(emp.monthly[m]) : <span className="salary-cell-muted">–</span>}
        </td>
      ))}
      <td className="col-total salary-calc-cell salary-row-total">
        {fmt(MONTHS.reduce((s, m) => s + emp.monthly[m], 0))}
      </td>
      <td className="col-del">
        <button className="salary-del-btn" onClick={() => requestRemove(section, idx, emp.employee_name)} aria-label={`Remove ${emp.employee_name || "employee"}`} disabled={editingDisabled}>
          <span className="material-icons">delete_outline</span>
        </button>
      </td>
    </tr>
  ));

  return (
    <div className="salary-view">
      {editingDisabled && (
        <div className="salary-locked-banner">
          <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 6 }}>lock</span>
          Salary editing is currently disabled. Contact the Finance Admin to open forecast planning.
        </div>
      )}
      {/* Header with save bar */}
      <div className="salary-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="salary-planner-badge">Planner</span>
          <span className="salary-header-sub-info">Account 60010 &mdash; Salaries &nbsp;|&nbsp; Account 60022 &mdash; Incentive Bonus (BIP)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isDirty && !saving && (
            <span className="salary-unsaved-banner">
              <span className="material-icons" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }}>warning_amber</span>
              You have unsaved changes — remember to save
            </span>
          )}
          {feedback && (
            <span className={`salary-feedback ${feedback.ok ? "salary-feedback--ok" : "salary-feedback--err"}`}>
              {feedback.msg}
            </span>
          )}
          <button
            className={`salary-save-btn${isDirty ? " salary-save-btn--active" : ""}`}
            onClick={handleSave}
            disabled={saving || !isDirty || editingDisabled}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="salary-raise-banner">
        <span className="salary-raise-label">Raise % Apr–Jun</span>
        <span className="salary-raise-value">{(config.raise_before_m3 * 100).toFixed(2)}%</span>
        <span className="salary-raise-sep">·</span>
        <span className="salary-raise-label">Raise % Jul–Mar</span>
        <span className="salary-raise-value">{(config.raise_after_m3 * 100).toFixed(2)}%</span>
        <span className="salary-raise-sep">·</span>
        <span className="salary-raise-label">BIP Raise</span>
        <span className="salary-raise-value">{(config.bip_raise * 100).toFixed(2)}%</span>
        <span className="salary-raise-note">set by Finance Admin</span>
      </div>

      <div className="salary-table-wrap">
        <table className="salary-table">
          <thead>
            <tr className="salary-thead-top">
              <th className="col-name">Employee Name</th>
              <th className="col-title">Job Title</th>
              <th className="col-salary">Annual Salary</th>
              <th className="col-bip-pct">BIP %</th>
              <th className="col-type">Emp. Type</th>
              <th className="col-hire-month">Start Mo.</th>
              <th className="col-status">Status</th>
              <th className="col-end-month">Term. Mo.</th>
              {MONTHS.map(m => <th key={m} className="col-month">{MONTH_ABBR[m]}</th>)}
              <th className="col-total">Total FY</th>
              <th className="col-del" />
            </tr>
          </thead>
          <tbody>
            {/* ── Existing Employees ── */}
            <tr
              className={`salary-section-header-row${dropTarget === "existing" && dragSource?.section === "newHire" ? " salary-section--drop-active" : ""}`}
              onDragOver={e => { if (dragSource?.section === "newHire") { e.preventDefault(); setDropTarget("existing"); } }}
              onDragLeave={() => { if (dropTarget === "existing") setDropTarget(null); }}
              onDrop={e => { e.preventDefault(); handleDrop("existing"); }}
            >
              <td colSpan={colCount}>
                <div className="salary-section-bar">
                  <div className="salary-section-bar-left">
                    <span className="salary-section-title">Current Employees</span>
                    <span className="salary-section-count">{existing.length}</span>
                    {dropTarget === "existing" && dragSource?.section === "newHire" && (
                      <span className="salary-drop-hint">
                        <span className="material-icons">arrow_downward</span>
                        Drop to move here
                      </span>
                    )}
                  </div>
                  <button className="salary-section-add-btn" onClick={addExisting} disabled={editingDisabled}>
                    <span className="material-icons">add</span>
                    Add Employee
                  </button>
                </div>
              </td>
            </tr>
            {existing.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="salary-empty-row">
                  No employees added yet.
                </td>
              </tr>
            ) : (
              employeeTableBody(existingWithMonthly, updateExisting, "existing", false)
            )}

            {/* ── New Hire Planning ── */}
            <tr
              className={`salary-section-header-row salary-newhire-header${dropTarget === "newHire" && dragSource?.section === "existing" ? " salary-section--drop-active" : ""}`}
              onDragOver={e => { if (dragSource?.section === "existing") { e.preventDefault(); setDropTarget("newHire"); } }}
              onDragLeave={() => { if (dropTarget === "newHire") setDropTarget(null); }}
              onDrop={e => { e.preventDefault(); handleDrop("newHire"); }}
            >
              <td colSpan={colCount}>
                <div className="salary-section-bar">
                  <div className="salary-section-bar-left">
                    <span className="salary-section-title">Planned New Hires</span>
                    <span className="salary-section-count salary-section-count--nh">{newHires.length}</span>
                    {dropTarget === "newHire" && dragSource?.section === "existing" && (
                      <span className="salary-drop-hint">
                        <span className="material-icons">arrow_downward</span>
                        Drop to move here
                      </span>
                    )}
                  </div>
                  <button className="salary-section-add-btn salary-section-add-btn--newhire" onClick={addNewHire} disabled={editingDisabled}>
                    <span className="material-icons">add</span>
                    Plan New Hire
                  </button>
                </div>
              </td>
            </tr>
            {newHires.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="salary-empty-row">
                  No new hires planned.
                </td>
              </tr>
            ) : (
              employeeTableBody(newHiresWithMonthly, updateNewHire, "newHire", true)
            )}
          </tbody>

          {/* Summary rows */}
          <tfoot>
            <tr className="salary-tfoot-spacer"><td colSpan={colCount} /></tr>
            <tr className="salary-summary-row salary-total-row">
              <td colSpan={8} className="salary-summary-label">Total Salary <span style={{ fontSize: 10, fontWeight: 400, color: "#64748b" }}>(60010)</span></td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">
                  {fmt(monthlyTotals[m])}
                </td>
              ))}
              <td className="col-total salary-calc-cell salary-row-total">
                {fmt(annualTotal)}
              </td>
              <td className="col-del" />
            </tr>
            <tr className="salary-summary-row salary-bip-total-row">
              <td colSpan={8} className="salary-summary-label">Total BIP <span style={{ fontSize: 10, fontWeight: 400, color: "#64748b" }}>(60022)</span></td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">
                  {fmt(bipMonthlyTotals[m])}
                </td>
              ))}
              <td className="col-total salary-calc-cell salary-row-total">
                {fmt(bipAnnualTotal)}
              </td>
              <td className="col-del" />
            </tr>
            {computedAccounts.map(ca => (
              <tr key={ca.account} className={`salary-summary-row salary-benefit-row salary-benefit-row--${ca.account}`}>
                <td colSpan={8} className="salary-summary-label">Total {ca.label} <span style={{ fontSize: 10, fontWeight: 400, color: "#64748b" }}>({ca.account})</span></td>
                {MONTHS.map(m => (
                  <td key={m} className="col-month salary-calc-cell">
                    {fmt(ca.months[m] || 0)}
                  </td>
                ))}
                <td className="col-total salary-calc-cell salary-row-total">
                  {fmt(ca.total)}
                </td>
                <td className="col-del" />
              </tr>
            ))}
            <tr className="salary-summary-row salary-hc-row">
              <td colSpan={8} className="salary-summary-label">Full-Time Headcount</td>
              {MONTHS.map(m => (
                <td key={m} className="col-month salary-calc-cell">{monthlyHeadcount[m]}</td>
              ))}
              <td className="col-total salary-calc-cell" />
              <td className="col-del" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Note */}
      <div className="salary-note">
        <span className="material-icons salary-note-icon">info_outline</span>
        <div className="salary-note-content">
          <p><strong>Salary:</strong> Monthly salary = (Annual Salary × (1 + Raise %)) / 12. Raise % for Apr–Jun and Jul–Mar are set by Finance Admin.</p>
          <p><strong>BIP:</strong> Monthly BIP = (Annual Salary × (1 + Raise %) × BIP % × (1 + BIP Raise)) / 12. Enter each employee's BIP % to compute their incentive bonus. Inactive employees receive no BIP after their end month.</p>
          <p><strong>Start month:</strong> Selecting a specific month zeros out salary and BIP for all prior months. Use "Full Year" for employees present the entire fiscal year.</p>
          <p><strong>Status &amp; End month:</strong> Set an employee to "Inactive/Moved" and select the last paid month. Salary and BIP accrual stops after that month.</p>
        </div>
      </div>

      {/* Actions */}
      <div className={`salary-actions${isDirty ? " salary-actions--sticky" : ""}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isDirty && !saving && (
            <>
              <span className="salary-unsaved-hint">
                <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>edit</span>
                Unsaved changes
              </span>
              <button className="salary-discard-btn" onClick={handleDiscard} disabled={saving}>
                Discard
              </button>
            </>
          )}
        </div>
        <div className="salary-actions-right">
          {feedback && (
            <span className={`salary-feedback ${feedback.ok ? "salary-feedback--ok" : "salary-feedback--err"}`}>
              {feedback.msg}
            </span>
          )}
          <button
            className={`salary-save-btn${isDirty ? " salary-save-btn--active" : ""}`}
            onClick={handleSave}
            disabled={saving || !isDirty || editingDisabled}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="salary-delete-overlay" onClick={() => setDeleteConfirm(null)} onKeyDown={e => { if (e.key === "Escape") setDeleteConfirm(null); }}>
          <div className="salary-delete-modal" role="dialog" aria-modal="true" aria-labelledby="salary-del-title" onClick={e => e.stopPropagation()}>
            <div className="salary-delete-modal-icon">
              <span className="material-icons">warning_amber</span>
            </div>
            <div className="salary-delete-modal-title" id="salary-del-title">Remove Employee</div>
            <div className="salary-delete-modal-msg">
              Are you sure you want to remove <strong>{deleteConfirm.name}</strong>? This will zero out their salary from the forecast.
            </div>
            <div className="salary-delete-modal-actions">
              <button className="salary-delete-modal-btn salary-delete-modal-btn--cancel" onClick={() => setDeleteConfirm(null)} autoFocus>
                Cancel
              </button>
              <button className="salary-delete-modal-btn salary-delete-modal-btn--confirm" onClick={confirmRemove}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryView;
